"use client";

import { useConsent } from "@packages/consent";
import { useSearch } from "@packages/framework";
import type { Geolocation, Me, SubscriptionDetail } from "@packages/sdk";
import {
  getCookieAjsAnonymousId,
  getCookieHomepageVariant,
  setCookieAjsAnonymousId,
  useDeviceAndUserProperties,
  useLocalStorageState,
  useRequestGeolocation,
  useRequestMe,
  useRequestSubscription,
  useSession,
} from "@packages/sdk";
import type { Mixpanel } from "mixpanel-browser";
import mixpanelBrowser from "mixpanel-browser";
import type { PropsWithChildren } from "react";
import { useEffect, useMemo, useState } from "react";

import { mixpanelContext } from "../lib/mixpanelContext";
import type { HallowABFlag } from "../types/enums/HallowABFlag.enum";
import { MixpanelABFlagValidProps } from "../types/enums/MixpanelABFlagValidProps.enum";

const THREE_SECONDS_IN_MS = 3000;
const MS_BETWEEN_ATTEMPTS = 250;
const ATTEMPTS = THREE_SECONDS_IN_MS / MS_BETWEEN_ATTEMPTS;

export type MixpanelLogicFlag = {
  type: "guest" | "user" | "subscriber";
  kind?: "feature" | "experiment";
  evaluate?: (props: {
    subscription?: SubscriptionDetail;
    geolocation?: Geolocation;
    user?: Me;
    searchParams?: URLSearchParams;
  }) => boolean;
};

const isFeatureFlag = (f?: MixpanelLogicFlag): boolean => f?.kind === "feature";
type ABProviderProps = PropsWithChildren<{
  flags: { [key in HallowABFlag]?: MixpanelLogicFlag };
}>;

export const ABProvider = ({ children, flags }: ABProviderProps) => {
  const search = useSearch();
  const deviceAndUserProperties = useDeviceAndUserProperties();
  const [mixpanel, setMixpanel] = useState<Mixpanel | null>(null);
  const [ready, setReady] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { isConsenting, isLoading: isConsentLoading } = useConsent();
  const { query: queryMe } = useRequestMe();
  const { query: querySubscription } = useRequestSubscription();
  const { query: queryGeolocation } = useRequestGeolocation();
  const [flagCache, setFlagCache] = useLocalStorageState<{
    [key in HallowABFlag]?: {
      isTargeted: boolean;
      variant: "control" | "treatment" | "excluded" | null;
    };
  }>({
    defaultValue: Object.keys(flags).reduce((prev, key) => {
      const f = flags[key as HallowABFlag];
      if (isFeatureFlag(f)) return prev;
      return { ...prev, [key]: { isTargeted: false, variant: null } };
    }, {}),
    key: "flags",
  });
  const [featureFlagValues, setFeatureFlagValues] = useState<{
    [key in HallowABFlag]?: {
      isTargeted: boolean;
      variant: "control" | "treatment" | "excluded" | null;
    };
  }>({});
  useSession({
    onUnauthenticated: () => {
      mixpanel?.reset();
      setFlagCache({});
      setFeatureFlagValues({});
    },
  });

  useEffect(() => {
    if (!queryMe.data?.id) return;
    const storedId = localStorage.getItem("flags_user_id");
    const parsedId = storedId !== null ? parseInt(storedId, 10) : null;
    if (parsedId !== null && parsedId !== queryMe.data.id) {
      setFlagCache({});
      setFeatureFlagValues({});
    }
    localStorage.setItem("flags_user_id", String(queryMe.data.id));
  }, [queryMe.data?.id]);

  //#region Context Updating
  /** @var {boolean} seenAsSubscriber - Indicates that the user has an active subscription */
  const seenAsSubscriber = !!querySubscription.data;

  const customProperties = useMemo(() => {
    const props: Record<string, string> = {};
    for (const [key, f] of Object.entries(flags)) {
      let validProp = MixpanelABFlagValidProps[key];
      if (!validProp) continue;
      if (!isFeatureFlag(f) && flagCache?.[key]?.variant) {
        props[validProp] = flagCache[key].variant;
      } else if (!queryGeolocation.data) {
        props[validProp] = "false";
      } else if (f.type === "user" && !queryMe.data?.id) {
        props[validProp] = "false";
      } else if (f.type === "subscriber" && !seenAsSubscriber) {
        props[validProp] = "false";
      } else {
        if (!f.evaluate) {
          props[validProp] = "true";
        } else {
          props[validProp] = f.evaluate({
            subscription: querySubscription.data,
            geolocation: queryGeolocation.data,
            user: queryMe.data,
            searchParams: search,
          })
            ? "true"
            : "false";
        }
      }
    }
    return {
      ...props,
      ...deviceAndUserProperties,
      homepageVariant: getCookieHomepageVariant() ?? "excluded",
    };
  }, [
    flags,
    querySubscription.data,
    queryGeolocation.data,
    queryMe.data,
    isConsenting,
    deviceAndUserProperties,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (mixpanel) {
      mixpanel.flags
        .update_context({
          custom_properties: customProperties,
        })
        .then(() => {
          if (cancelled) return;
          const experimentValues: {
            [key in HallowABFlag]?: {
              isTargeted: boolean;
              variant: "control" | "treatment" | "excluded" | null;
            };
          } = {};
          const featureValues: {
            [key in HallowABFlag]?: {
              isTargeted: boolean;
              variant: "control" | "treatment" | "excluded" | null;
            };
          } = {};

          for (const [key, f] of Object.entries(flags)) {
            const shouldCheck =
              (f.type === "guest" && !!queryGeolocation.data) ||
              (f.type === "user" && !!queryMe.data?.id) ||
              (f.type === "subscriber" && seenAsSubscriber);

            if (isFeatureFlag(f)) {
              if (shouldCheck) {
                const variant = mixpanel.flags.get_variant_value_sync(
                  key,
                  "excluded",
                );
                featureValues[key] = {
                  isTargeted: variant === true || variant === "treatment",
                  variant,
                };
              } else {
                featureValues[key] = { isTargeted: false, variant: null };
              }
            } else {
              let variant = flagCache?.[key]?.variant;
              if (shouldCheck) {
                variant =
                  variant ??
                  mixpanel.flags.get_variant_value_sync(key, "excluded");
                experimentValues[key] = {
                  isTargeted: variant === true || variant === "treatment",
                  variant,
                };
              } else {
                experimentValues[key] = {
                  isTargeted: variant ? variant === "treatment" : false,
                  variant: variant ?? null,
                };
              }
            }
          }

          setFlagCache(experimentValues);
          setFeatureFlagValues(featureValues);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [customProperties, mixpanel]);

  useEffect(() => {
    if (error) {
      const excluded = { isTargeted: false, variant: "excluded" as const };
      const newExperimentCache = {};
      const newFeatureValues = {};
      for (const key of Object.keys(flags)) {
        if (isFeatureFlag(flags[key as HallowABFlag]))
          newFeatureValues[key] = excluded;
        else newExperimentCache[key] = excluded;
      }
      setFlagCache(newExperimentCache);
      setFeatureFlagValues(newFeatureValues);
    }
  }, [error]);
  //#endregion

  //#region Library Initialization
  useEffect(() => {
    function handleLoadingMp(mp: Mixpanel) {
      setMixpanel(mp);
      const ajsAnonymousId = getCookieAjsAnonymousId();
      if (ajsAnonymousId) {
        mp.alias(ajsAnonymousId);
      } else {
        setCookieAjsAnonymousId({ value: mp.get_distinct_id() });
      }
    }

    if (!isConsentLoading && isConsenting) {
      try {
        try {
          const distinctId = mixpanelBrowser.get_distinct_id();
          if (distinctId) {
            // if this worked, then it's already loaded and calling init() won't call the loaded() function again
            handleLoadingMp(mixpanelBrowser);
            return;
          }
        } catch {}
        // but if it didn't work, it probably threw and we need to initialize
        mixpanelBrowser.init(process.env.DOPPLER_PUBLIC_MIXPANEL_TOKEN, {
          debug: process.env.NODE_ENV === "development",
          flags: {
            context: {
              custom_properties: customProperties,
            },
          },
          loaded: handleLoadingMp,
        });
      } catch (e) {
        console.error(e);
        setError(true);
      }
    } else if (!isConsentLoading && !isConsenting) {
      setError(true);
    }
  }, [isConsenting, isConsentLoading, deviceAndUserProperties]);

  useEffect(() => {
    let interval = null;
    if (mixpanel) {
      if (mixpanel.flags.are_flags_ready()) {
        setReady(true);
      } else {
        let count = 0;
        interval = setInterval(() => {
          if (++count > ATTEMPTS) {
            setError(true);
            clearInterval(interval);
          } else if (mixpanel.flags?.are_flags_ready()) {
            setReady(true);
            clearInterval(interval);
          }
        }, MS_BETWEEN_ATTEMPTS);
      }
    }

    return () => {
      clearInterval(interval);
    };
  }, [mixpanel]);
  //#endregion

  return (
    <mixpanelContext.Provider
      value={{
        flags: { ...flagCache, ...featureFlagValues },
        isLoading: !ready && !error,
      }}
    >
      {children}
    </mixpanelContext.Provider>
  );
};
